import { builtEnvironment, isRunningLocal } from '@shared';
import { store } from '../model/store';

const BATCH_SIZE = 10;
const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE_SIZE = 100;

const sessionId = generateSessionId();

let eventQueue: AuthenticatedEventPayload[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function generateSessionId(): string {
  const stored = sessionStorage.getItem('analytics_session_id');
  if (stored) return stored;
  
  const newId = `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  sessionStorage.setItem('analytics_session_id', newId);
  return newId;
}

function getUserContext(): { userId: string | null; userType: EventUserType } {
  try {
    const state = store.getState();
    const session = state.session;
    
    if (session?.advisor?.advisorId) {
      return { userId: session.advisor.advisorId, userType: 'advisor' };
    }
    
    if (session?.clientProfiles && session.clientProfiles.length > 0) {
      return { userId: session.clientProfiles[0].clientId, userType: 'client' };
    }

    const clientData = state?.client?.data;
    if (clientData?.clientId) {
      return { userId: clientData.clientId, userType: 'client' };
    }
    
    return { userId: null, userType: 'anonymous' };
  } catch {
    return { userId: null, userType: 'anonymous' };
  }
}

function getPageContext(): Pick<EventPayload, 'pageUrl' | 'pagePath' | 'pageTitle' | 'referrer'> {
  return {
    pageUrl: window.location.href,
    pagePath: window.location.pathname,
    pageTitle: document.title,
    referrer: document.referrer || undefined,
  };
}

function getApiUrl(endpoint: string): string {
  const origin = window.location.origin;
  const env = isRunningLocal() ? 'dev' : builtEnvironment;
  return `${origin}/${env}/api/public/${endpoint}`;
}

async function sendEvents(events: AuthenticatedEventPayload[]): Promise<void> {
  if (events.length === 0) return;

  try {
    const url = events.length === 1 
      ? getApiUrl('event')
      : getApiUrl('event-batch');
    
    const body = events.length === 1 
      ? events[0]
      : { events };

    await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch (error) {
    console.debug('[Analytics] Failed to send events:', error);
  }
}

function flush(): void {
  if (eventQueue.length === 0) return;

  const eventsToSend = [...eventQueue];
  eventQueue = [];
  
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  sendEvents(eventsToSend);
}

function scheduleFlush(): void {
  if (flushTimer) return;
  
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

function queueEvent(event: AuthenticatedEventPayload): void {
  if (eventQueue.length >= MAX_QUEUE_SIZE) {
    flush();
  }

  eventQueue.push(event);

  if (eventQueue.length >= BATCH_SIZE) {
    flush();
  } else {
    scheduleFlush();
  }
}

export const analytics = {
  track(
    eventName: string,
    properties?: BaseEventProperties,
    category?: EventCategory,
    eventSessionId?: string,
  ): void {
    const { userId, userType } = getUserContext();
    const pageContext = getPageContext();

    const event: AuthenticatedEventPayload = {
      eventName,
      eventCategory: category,
      userId: userId ?? undefined,
      userType,
      sessionId: eventSessionId ?? sessionId,
      timestamp: new Date().toISOString(),
      ...pageContext,
      properties,
    };

    queueEvent(event);
  },

  page(properties?: BaseEventProperties): void {
    this.track('page_view', properties, 'navigation');
  },

  click(elementId: string, properties?: BaseEventProperties): void {
    this.track('click', { elementId, ...properties }, 'engagement');
  },

  formSubmit(formId: string, properties?: BaseEventProperties): void {
    this.track('form_submit', { formId, ...properties }, 'action');
  },

  error(errorMessage: string, properties?: BaseEventProperties): void {
    this.track('error', { errorMessage, ...properties }, 'error');
  },

  identify(userId: string, userType: EventUserType, traits?: BaseEventProperties): void {
    this.track('identify', { userId, userType, ...traits }, 'system');
  },

  inviteClients(properties?: BaseEventProperties & { 
    clientIds?: string[];
    clientCount?: number;
    inviteMethod?: string;
  }): void {
    this.track('invite_clients', properties, 'action');
  },

  createPlan(properties?: BaseEventProperties & {
    clientId?: string;
    planType?: string;
    planId?: string;
  }): void {
    this.track('insurance_application_created', properties, 'action');
  },

  submitPlan(properties?: BaseEventProperties & {
    clientId?: string;
    planType?: string;
    planId?: string;
  }): void {
    this.track('insurance_application_submitted', properties, 'action');
  },

  appOpen(properties?: BaseEventProperties & {
    entryPoint?: string;
    isFirstOpen?: boolean;
  }, authenticationSessionId?: string): void {
    this.track('app_open', properties, 'navigation', authenticationSessionId);
  },

  flush,

  getSessionId(): string {
    return sessionId;
  },
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flush);
  window.addEventListener('pagehide', flush);
  
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush();
    }
  });
}

export default analytics;
